import { Dispatch, SetStateAction, useState } from "react";
import { Box, Button, Stack } from "@mui/material";
import {
  SectionHeader,
  ColumnVisibilityFilter,
  SearchFilter,
} from "@shared/components";
import type { FormikHelpers } from "formik";
import { Add } from "@mui/icons-material";
import { useCreateTitleConfiguration } from "../services/useCreateTitleConfiguration";
import {
  ColumnKey,
  ColumnOptions,
  mapApiMessageToFormikForm,
} from "../utils/constants";
import { FilterField, TitleFormValues } from "../utils/types";
import TitleForm from "./TitleForm";
import { FormActions } from "@shared/utils/constant";
import { Can } from "@core/casl-provider";

interface TitleHeaderProps {
  setPaginationModel: Dispatch<
    SetStateAction<{ page: number; pageSize: number }>
  >;
  visibleColumns: ColumnKey[] | string[];
  setVisibleColumns: Dispatch<SetStateAction<ColumnKey[]>>;
}

const filterFields: FilterField[] = [
  {
    id: "title-search",
    name: "searchQuery",
    label: "Search Title Configuration",
    placeholder: "Search Title Configuration...",
    clearAriaLabel: "Clear Search",
  },
];

const TitleHeader = ({
  visibleColumns,
  setVisibleColumns,
}: TitleHeaderProps) => {
  const [actionState, setActionState] = useState<{
    action: FormActions.ADD | "";
    payload: TitleFormValues | object;
  }>({
    action: "",
    payload: {} as TitleFormValues,
  });

  const { mutateAsync: createTitleConfigurationAsync, isPending } =
    useCreateTitleConfiguration();

  const clearActionState = () => {
    setActionState({ action: "", payload: {} });
  };

  // Column visibility handled by shared component
  const handleSubmit = async (
    values: TitleFormValues,
    helpers: FormikHelpers<TitleFormValues & { id?: number }>
  ) => {
    try {
      // Transform form values to API payload:
      const { id, pushEnabled, isActive, ...rest } = values;
      const payload = {
        ...rest,
        id: id ? String(id) : undefined,
        pushEnabled: !!pushEnabled,
        isActive: !!isActive,
      };
      const response = await createTitleConfigurationAsync({ payload });
      if (response?.statusCode >= 400) {
        const rawMsg =
          response?.message || "Failed to create title configuration";
        const { errors, touched } = mapApiMessageToFormikForm(String(rawMsg));
        helpers.setErrors(errors);
        helpers.setTouched(touched, false);
        return;
      }
      // Success
      clearActionState();
    } catch (error) {
      const errMsg = (error as Error)?.message || "Something went wrong";
      const { errors, touched } = mapApiMessageToFormikForm(String(errMsg));
      helpers.setErrors(errors);
      helpers.setTouched(touched, false);
      console.error("Failed to create title configuration:", errMsg);
      // error notifications are also handled in the hook's onError
    } finally {
      helpers.setSubmitting(false);
    }
  };
  return (
    <Box>
      <SectionHeader title="Title Configuration" />

      <Stack
        direction="row"
        spacing={2}
        justifyContent="space-between"
        mt={3}
        alignItems="center"
        sx={(theme) => ({
          border: `1px solid ${theme.palette.divider}`,
          borderBottom: 0,
          backgroundColor: "background.paper",
          padding: "15px",
          borderRadius: "4px 4px 0 0",
        })}
      >
        <SearchFilter filterFields={filterFields} />

        <Stack
          direction="row"
          spacing={2}
          alignItems="center"
        >
          <ColumnVisibilityFilter
            value={visibleColumns as string[]}
            onChange={(vals) => setVisibleColumns(vals as ColumnKey[])}
            options={ColumnOptions}
            size="medium"
          />

          <Can
            do={"create"}
            on={"TitleConfiguration"}
          >
            <Button
              variant="contained"
              sx={{ height: 38, padding: "0 20px" }}
              onClick={() =>
                setActionState({ action: FormActions.ADD, payload: {} })
              }
              startIcon={<Add />}
            >
              Add New Title
            </Button>
          </Can>
        </Stack>
      </Stack>
      <TitleForm
        open={actionState.action === FormActions.ADD}
        onClose={clearActionState}
        title={"Add Title Configuration"}
        disable={isPending}
        initialValues={actionState.payload}
        onSubmit={handleSubmit}
        showCloseButton
      />
    </Box>
  );
};

export default TitleHeader;
